import React, { useState, useEffect, useCallback } from 'react';
import { donorsService, grantsService, donationsService, organizationsService } from '../services/api';
import './AuditHistory.css';

const AuditHistory = ({ entityId, entityType = 'donor' }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAuditHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      
      switch (entityType) {
        case 'donor':
          response = await donorsService.getAuditHistory(entityId);
          break;
        case 'grant':
          response = await grantsService.getAuditHistory(entityId);
          break;
        case 'donation':
          response = await donationsService.getAuditHistory(entityId);
          break;
        case 'organization':
          response = await organizationsService.getAuditHistory(entityId);
          break;
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }
      
      setAuditLogs(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch audit history');
      console.error('Error fetching audit history:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    fetchAuditHistory();
  }, [fetchAuditHistory]);

  const getActivityIcon = (activity) => {
    switch (activity.toLowerCase()) {
      case 'create':
        return '➕';
      case 'update':
        return '✏️';
      case 'delete':
        return '🗑️';
      default:
        return '•';
    }
  };

  const getActivityColor = (activity) => {
    switch (activity.toLowerCase()) {
      case 'create':
        return 'create';
      case 'update':
        return 'update';
      case 'delete':
        return 'delete';
      default:
        return 'default';
    }
  };

  if (loading) return <div className="loading">Loading audit history...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="audit-history-container">
      <h3>Audit History</h3>
      
      {auditLogs.length === 0 ? (
        <div className="empty-state">
          <p>No changes recorded yet.</p>
        </div>
      ) : (
        <div className="audit-timeline">
          {auditLogs.map((log, index) => (
            <div key={index} className="audit-entry">
              <div className="audit-marker">
                <span className={`activity-badge ${getActivityColor(log.activity_type)}`}>
                  {getActivityIcon(log.activity_type)}
                </span>
              </div>

              <div className="audit-content">
                <div className="audit-header">
                  <span className="activity-type">
                    {log.activity_type.charAt(0).toUpperCase() + log.activity_type.slice(1)}
                  </span>
                  <span className="timestamp">
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="audit-user">
                  Changed by: <strong>{log.first_name || 'System'} {log.last_name || ''}</strong>
                </div>

                {log.description && (
                  <div className="audit-description">{log.description}</div>
                )}

                {log.activity_type === 'update' && log.old_values && log.new_values && (
                  <div className="audit-changes">
                    <details>
                      <summary>View Changes</summary>
                      <div className="changes-detail">
                        {Object.entries(JSON.parse(log.new_values)).map(([key, newValue]) => {
                          const oldValue = JSON.parse(log.old_values)[key];
                          if (oldValue !== newValue) {
                            return (
                              <div key={key} className="change-item">
                                <strong>{formatFieldName(key)}:</strong>
                                <div className="change-values">
                                  <span className="old-value">
                                    {formatValue(oldValue)} → {formatValue(newValue)}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatFieldName = (field) => {
  return field
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatValue = (value) => {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export default AuditHistory;
